import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useState, useMemo, useEffect } from 'react';

// Компонент для снежинок
function Snowflake({ delay, duration, left }: { delay: number; duration: number; left: number }) {
    return (
        <div
            className="absolute text-white text-2xl opacity-70 pointer-events-none"
            style={{
                left: `${left}%`,
                animation: `fall ${duration}s linear ${delay}s infinite`,
                top: '-50px',
            }}
        >
            ❄
        </div>
    );
}

export default function Landing() {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const CORRECT_CODE = 'HGF2KR39';

    const calculatePeopleCount = () => {
        const baseDate = new Date('2026-01-01T00:00:00');
        const currentDate = new Date();
        const baseCount = 13889; // Базовое значение на 1 января 2026
        const dailyIncrease = 2748; // Количество человек в день

        // Вычисляем разницу в миллисекундах
        const diffTime = currentDate.getTime() - baseDate.getTime();

        // Вычисляем количество человек в миллисекунду для плавного обновления
        const peoplePerMillisecond = dailyIncrease / (1000 * 60 * 60 * 24); // человек в миллисекунду

        // Вычисляем итоговое количество с точностью до миллисекунд
        const totalCount = baseCount + (diffTime * peoplePerMillisecond);

        // Форматируем число с пробелами для тысяч (округление до целого)
        return Math.floor(totalCount).toLocaleString('ru-RU');
    };

    // Вычисляем количество людей на основе текущего времени
    const [peopleCount, setPeopleCount] = useState(calculatePeopleCount());

    useEffect(() => {
        // Обновляем значение каждую минуту для плавного изменения
        const interval = setInterval(() => {
            setPeopleCount(calculatePeopleCount());
        }, 60 * 1000); // Каждую минуту

        return () => clearInterval(interval);
    }, []);

    // Генерируем снежинки один раз при монтировании компонента
    const snowflakes = useMemo(() => {
        return Array.from({ length: 10 }, () => ({
            delay: Math.random() * 5,
            duration: 5 + Math.random() * 10,
            left: Math.random() * 100,
        }));
    }, []);

    // Генерируем звезды один раз при монтировании компонента
    const stars = useMemo(() => {
        return Array.from({ length: 100 }, (_, i) => ({
            id: i,
            left: Math.random() * 100,
            top: Math.random() * 100,
            width: Math.random() * 3 + 1,
            height: Math.random() * 3 + 1,
            delay: Math.random() * 3,
            duration: 2 + Math.random() * 2,
        }));
    }, []);

    const handleSpinClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (code.trim() === CORRECT_CODE) {
            setError('');
            navigate('/wheel');
        } else {
            setError('Код неверен');
        }
    };

    return (
        <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-slate-900 via-blue-900 to-indigo-900 dark:from-gray-950 dark:via-blue-950 dark:to-indigo-950">
            {/* Новогодний фон с звездами */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                {stars.map((star) => (
                    <div
                        key={star.id}
                        className="absolute rounded-full bg-white opacity-60 animate-twinkle"
                        style={{
                            left: `${star.left}%`,
                            top: `${star.top}%`,
                            width: `${star.width}px`,
                            height: `${star.height}px`,
                            animationDelay: `${star.delay}s`,
                            animationDuration: `${star.duration}s`,
                        }}
                    />
                ))}
            </div>

            {/* Анимация падающего снега */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-10">
                {snowflakes.map((flake, i) => (
                    <Snowflake key={i} delay={flake.delay} duration={flake.duration} left={flake.left} />
                ))}
            </div>

            {/* CSS для анимации снега */}
            <style>{`
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(360deg);
          }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        .animate-twinkle {
          animation: twinkle 2s ease-in-out infinite;
        }
      `}</style>

            {/* Шапка сайта в стиле Kukmara */}
            <header className="relative z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between h-16 md:h-20">
                        {/* Логотип */}
                        <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-white rounded-lg shadow-md border border-gray-200">
                                <span className="text-red-600 font-bold text-4xl md:text-xl">K</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-lg md:text-xl font-bold text-red-600 dark:text-red-500 leading-tight">
                                    KUKMARA
                                </span>
                                <span className="text-xs text-gray-600 dark:text-gray-400 leading-tight">
                                    Колесо Фортуны
                                </span>
                            </div>
                        </div>

                        {/* Навигация */}
                        <nav className="hidden md:flex items-center gap-6">
                            <Link
                                to="/"
                                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
                            >
                                Главная
                            </Link>
                            <Link
                                to="/wheel"
                                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
                            >
                                Колесо
                            </Link>
                        </nav>

                        {/* Контакты */}
                        <div className="flex items-center gap-4">
                            <div className="hidden lg:flex flex-col items-end">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Бесплатно по России</span>
                                <a
                                    href="tel:+78003339345"
                                    className="text-lg font-bold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                >
                                    +7 (800) 333-93-45
                                </a>
                            </div>
                            <div className="lg:hidden">
                                <a
                                    href="tel:+78003339345"
                                    className="text-base font-bold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                >
                                    +7 (800) 333-93-45
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Контент с относительным позиционированием */}
            <div className="relative z-20">
                {/* Hero Section */}
                <section className="container mx-auto px-4 py-12 md:py-20">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]">
                            Крути колесо и лови сказочный куш
                        </h1>
                        <div className="flex flex-col md:flex-row items-center justify-center gap-2 mb-8">
                            <div className="text-xl md:text-2xl font-semibold text-yellow-200">
                                <span className="text-red-400 font-bold drop-shadow-lg">{peopleCount}</span> человек
                            </div>
                            <div className="text-lg md:text-xl text-yellow-200">
                                уже крутили колесо!
                            </div>
                        </div>
                        <div className="mb-8">
                            <div className="max-w-md mx-auto mb-4">
                                <Input
                                    type="text"
                                    placeholder="Введите промокод"
                                    value={code}
                                    onChange={(e) => {
                                        setCode(e.target.value);
                                        setError('');
                                    }}
                                    className="w-full text-lg px-4 w-[90%] h-12 py-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-2 border-yellow-300/50 focus:border-yellow-400 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSpinClick(e as any);
                                        }
                                    }}
                                />
                                {error && (
                                    <p className="text-red-400 text-sm mt-2 text-center font-semibold">
                                        {error}
                                    </p>
                                )}
                            </div>
                            <Button
                                size="lg"
                                onClick={handleSpinClick}
                                className="text-xl md:text-2xl px-8 md:px-12 py-6 md:py-8 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                            >
                                Крутить колесо
                            </Button>
                        </div>
                    </div>

                    {/* Stats Section */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
                        <Card className="text-center p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-lg border-2 border-yellow-300/50">
                            <CardContent className="pt-6">
                                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">100+</div>
                                <p className="text-gray-700 dark:text-gray-300">призов</p>
                            </CardContent>
                        </Card>
                        <Card className="text-center p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-lg border-2 border-green-300/50">
                            <CardContent className="pt-6">
                                <div className="text-4xl font-bold text-green-600 dark:text-green-400 mb-2">1</div>
                                <p className="text-gray-700 dark:text-gray-300">попытка за каждый промокод</p>
                            </CardContent>
                        </Card>
                        <Card className="text-center p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-lg border-2 border-purple-300/50">
                            <CardContent className="pt-6">
                                <div className="text-4xl font-bold text-purple-600 dark:text-purple-400 mb-2">∞</div>
                                <p className="text-gray-700 dark:text-gray-300">возможностей выиграть</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Receipt Scanning Section */}
                    <Card className="mb-16 bg-gradient-to-r from-yellow-100/90 to-orange-100/90 dark:from-yellow-900/40 dark:to-orange-900/40 backdrop-blur-sm border-2 border-yellow-300 dark:border-yellow-600 shadow-xl">
                        <CardContent className="p-8">
                            <div className="flex flex-col md:flex-row items-center gap-6">
                                <div className="flex-1 text-center md:text-left">
                                    <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 dark:text-white drop-shadow-sm">
                                        Покупайте больше товаров от KUKMARA, находите промокоды и получайте еще больше шансов выиграть призы!
                                    </h2>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* How to Play Section */}
                    <div className="mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white drop-shadow-lg">
                            Будь в курсе всех акций и новостей
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-lg border-2 border-purple-300/50 hover:border-purple-400 transition-colors">
                                <CardContent className="p-6">
                                    <div className="text-5xl mb-4 text-center">📱</div>
                                    <h3 className="text-xl font-bold mb-3 text-center text-gray-900 dark:text-white">
                                        Подписывайся на ТГ канал KUKMARA
                                    </h3>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* Prizes Section */}
                    <div className="mb-16">
                        <div className="text-center mb-4">
                            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white drop-shadow-lg">
                                Не упусти шанс выиграть
                            </h2>
                            <p className="text-lg text-yellow-200/90 max-w-2xl mx-auto">
                                Крути колесо и получай потрясающие призы от KUKMARA!
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
                            {/* Супер-призы */}
                            <Card className="group relative overflow-hidden bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 text-white shadow-2xl hover:shadow-yellow-500/50 transition-all duration-300 transform hover:scale-105 hover:-translate-y-2 border-2 border-yellow-400/50">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12 blur-xl"></div>
                                <CardContent className="p-8 relative z-10">
                                    <div className="flex items-center justify-center mb-6">
                                        <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border-2 border-white/30">
                                            <span className="text-5xl">🏆</span>
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-bold mb-4 text-center drop-shadow-lg">
                                        Супер-призы
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                                            <span className="text-2xl">📱</span>
                                            <span className="font-semibold">iPhone 17 Pro, 256 GB</span>
                                        </div>
                                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                                            <span className="text-2xl">🎮</span>
                                            <span className="font-semibold">PlayStation 5 Slim</span>
                                        </div>
                                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                                            <span className="text-2xl">🔊</span>
                                            <span className="font-semibold">Умная колонка SberBoom</span>
                                        </div>
                                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                                            <span className="text-2xl">✨</span>
                                            <span className="font-semibold">И секретный СУПЕРПРИЗ!</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Ценные призы */}
                            <Card className="group relative overflow-hidden bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600 text-white shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:-translate-y-2 border-2 border-blue-400/50">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12 blur-xl"></div>
                                <CardContent className="p-8 relative z-10">
                                    <div className="flex items-center justify-center mb-6">
                                        <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border-2 border-white/30">
                                            <span className="text-5xl">💎</span>
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-bold mb-4 text-center drop-shadow-lg">
                                        Ценные призы
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                                            <span className="text-2xl">🎁</span>
                                            <span className="font-semibold">Подарочные сертификаты</span>
                                        </div>
                                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                                            <span className="text-2xl">🎬</span>
                                            <span className="font-semibold">Билеты в кино</span>
                                        </div>
                                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                                            <span className="text-2xl">🛍️</span>
                                            <span className="font-semibold">Скидки в магазинах</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Гарантированные призы */}
                            <Card className="group relative overflow-hidden bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 text-white shadow-2xl hover:shadow-green-500/50 transition-all duration-300 transform hover:scale-105 hover:-translate-y-2 border-2 border-green-400/50">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12 blur-xl"></div>
                                <CardContent className="p-8 relative z-10">
                                    <div className="flex items-center justify-center mb-6">
                                        <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border-2 border-white/30">
                                            <span className="text-5xl">🎯</span>
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-bold mb-4 text-center drop-shadow-lg">
                                        Гарантированные призы
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                                            <span className="text-2xl">🎫</span>
                                            <span className="font-semibold">Купоны на скидки</span>
                                        </div>
                                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                                            <span className="text-2xl">📺</span>
                                            <span className="font-semibold">Подписки в подарок</span>
                                        </div>
                                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                                            <span className="text-2xl">💰</span>
                                            <span className="font-semibold">Скидки на товары</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <div className="mt-20 mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white drop-shadow-lg">
                            Вопрос — ответ
                        </h2>
                        <div className="space-y-4 max-w-4xl mx-auto">
                            <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-md border border-white/20">
                                <CardContent className="p-6">
                                    <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                                        Какие сроки Акции?
                                    </h3>
                                    <p className="text-gray-700 dark:text-gray-300">
                                        Акция «Колесо Фортуны» проходит с 30 октября 2025 по 14 января 2026 включительно.
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-md border border-white/20">
                                <CardContent className="p-6">
                                    <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                                        Как принять участие в Акции?
                                    </h3>
                                    <p className="text-gray-700 dark:text-gray-300">
                                        Введи промокод, чтобы перейти на страницу с колесом и нажми кнопку «Крутить колесо». За каждую покупку, удовлетворяющую условиям акции, тебе предоставляется одна возможность прокрутки колеса.
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-md border border-white/20">
                                <CardContent className="p-6">
                                    <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                                        Кто может принять участие в Акции?
                                    </h3>
                                    <p className="text-gray-700 dark:text-gray-300">
                                        Участником акции может стать любой гражданин РФ старше 18 лет. Юридические лица, индивидуальные предприниматели, сотрудники KUKMARA (в том числе работники магазинов-арендаторов) и представители акции не могут стать участниками розыгрыша.
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* Footer */}
                    <footer className="text-center py-8 border-t border-white/20">
                        <p className="text-yellow-200 mb-2">
                            © 2013-2026 KUKMARA
                        </p>
                        <p className="text-yellow-200">
                            Контактный центр: +7 (800) 333-93-45
                        </p>
                    </footer>
                </section>
            </div>
        </div>
    );
}

